import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ServiceSuggestionsByCategory, ShortlistItemInput } from '@om/shared-types';
import { serviceSuggestionApiService } from '@/lib/api/serviceSuggestion.service';
import { CategoryAccordion } from '@/components/services/CategoryAccordion';
import { Loader2, ListChecks } from 'lucide-react';
import { PageContainer } from '@/components/layout/PageContainer';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';

interface SelectionState {
  id: number;
  selected: boolean;
  priority: 'must_have' | 'optional';
}

type CategorySelections = Record<string, SelectionState[]>;

export function ServiceSelectionHub() {
  const { celebrationId } = useParams<{ celebrationId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['services']);
  const cid = Number(celebrationId);

  const [categories, setCategories] = useState<ServiceSuggestionsByCategory[]>([]);
  const [selections, setSelections] = useState<CategorySelections>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        const cats = await serviceSuggestionApiService.getSuggestions(cid);
        if (!Array.isArray(cats)) {
          throw new Error('Invalid service suggestions response');
        }

        const normalizedCategories = cats.filter(
          (category): category is ServiceSuggestionsByCategory =>
            Boolean(
              category
              && typeof category === 'object'
              && typeof category.category === 'string'
              && Array.isArray(category.suggestions)
            )
        );

        setCategories(normalizedCategories);

        // Initialize selections from pre-selected suggestions
        const initial: CategorySelections = {};
        for (const cat of normalizedCategories) {
          initial[cat.category] = cat.suggestions.map((s) => ({
            id: s.id,
            selected: s.is_selected,
            priority: s.priority ?? 'optional',
          }));
        }
        setSelections(initial);
      } catch (e: any) {
        setError(e.message ?? t('services:selectionHub.errors.loadSuggestions'));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [cid]);

  const handleCategoryChange = useCallback(
    (category: string, catSelections: SelectionState[]) => {
      setSelections((prev) => ({ ...prev, [category]: catSelections }));
    },
    []
  );

  async function handleSave() {
    setSaving(true);
    try {
      const items: ShortlistItemInput[] = Object.values(selections)
        .flat()
        .filter((s) => s.selected)
        .map((s) => ({
          service_definition_id: s.id,
          priority: s.priority,
          is_custom_addition: false,
        }));
      await serviceSuggestionApiService.saveShortlist(cid, { items });
    } catch (e: any) {
      setError(e.message ?? t('services:selectionHub.errors.saveShortlist'));
    } finally {
      setSaving(false);
    }
  }

  async function handleFinalize() {
    setFinalizing(true);
    try {
      // Save first, then finalize
      const items: ShortlistItemInput[] = Object.values(selections)
        .flat()
        .filter((s) => s.selected)
        .map((s) => ({
          service_definition_id: s.id,
          priority: s.priority,
          is_custom_addition: false,
        }));
      await serviceSuggestionApiService.saveShortlist(cid, { items });
      await serviceSuggestionApiService.finalizeShortlist(cid);
      navigate(`/celebrations/${cid}/services`);
    } catch (e: any) {
      setError(e.message ?? t('services:selectionHub.errors.finalizeShortlist'));
    } finally {
      setFinalizing(false);
    }
  }

  const totalSelected = Object.values(selections)
    .flat()
    .filter((s) => s.selected).length;

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <span className="ml-2 text-sm text-gray-500">{t('services:selectionHub.loading')}</span>
      </div>
    );
  }

  return (
    <PageContainer width="wide">
      <AppBreadcrumbs />

      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <ListChecks className="h-6 w-6 text-primary" />
            {t('services:selectionHub.title')}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('services:selectionHub.subtitle')}
          </p>
        </div>
        <span className="rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
          {t('services:selectionHub.selected', { count: totalSelected })}
        </span>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="space-y-3">
        {categories.map((cat) => (
          <CategoryAccordion
            key={cat.category}
            category={cat.category}
            services={cat.suggestions}
            defaultOpen={true}
            onSelectionChange={handleCategoryChange}
          />
        ))}
      </div>

      <div className="mt-6 flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {saving ? t('services:selectionHub.saving') : t('services:selectionHub.saveDraft')}
        </button>
        <button
          type="button"
          onClick={handleFinalize}
          disabled={finalizing || saving}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
        >
          {finalizing ? t('services:selectionHub.finalizing') : t('services:selectionHub.finalizeShortlist')}
        </button>
      </div>
    </PageContainer>
  );
}
